import React, { useState, useEffect } from "react";
import { Box, Typography, Stack } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CircleIcon from "@mui/icons-material/Circle";
import { useSelector } from "react-redux";
import { fetchRevenuePlanWise } from "../../../api/region";
import { fetchRevenueNyPlanWise } from "../../../api/nystats";

const RevenueChartNewYork = () => {
	const [revenueData, setRevenueData] = useState([]);
	const [totalRevenue, setTotalRevenue] = useState(0);
	const [totalUsers, setTotalUsers] = useState(0);
	const [marketingStats, setMarketingStats] = useState(null);
	const [organicStats, setOrganicStats] = useState(null);
	const [isLoading, setIsLoading] = useState(false);

	const { filter } = useSelector((state) => state);

	const fetchData = async () => {
		try {
			setIsLoading(true);

			const { data, marketingStats, organicStats } = await fetchRevenueNyPlanWise(filter);
			const totalRev = data.reduce((sum, d) => sum + d.totalRevenue, 0);
			const totalUsr = data.reduce((sum, d) => sum + d.subscriptionCount, 0);

			setRevenueData(data);
			setTotalRevenue(totalRev);
			setTotalUsers(totalUsr);
			setMarketingStats(marketingStats || null);
			setOrganicStats(organicStats || null);
		} catch (err) {
			console.error("Error while loading New York revenue data", err);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchData();
	}, [filter]);

	// --------------------------
	// Helpers
	// --------------------------
	const getValue = (type, field) =>
		revenueData.find((d) => d.subscriptionType === type)?.[field] || 0;

	const getSourcePlan = (sourceStats, planName) => {
		if (!sourceStats || !sourceStats.plans) {
			return { totalRevenue: 0, revenueShareOfPlan: "0.00" };
		}
		return (
			sourceStats.plans.find((p) => p.subscriptionType === planName) || {
				totalRevenue: 0,
				revenueShareOfPlan: "0.00",
			}
		);
	};

	// Revenues
	const goldRevenue = getValue("Gold", "totalRevenue");
	const luxuryRevenue = getValue("Luxury", "totalRevenue");
	const freeRevenue = getValue("Free", "totalRevenue");

	// Counts
	const goldCount = getValue("Gold", "subscriptionCount");
	const luxuryCount = getValue("Luxury", "subscriptionCount");
	const freeCount = getValue("Free", "subscriptionCount");

	// Percentages (based on count of users)
	const goldPercent = totalUsers ? (goldCount / totalUsers) * 100 : 0;
	const luxuryPercent = totalUsers ? (luxuryCount / totalUsers) * 100 : 0;
	const freePercent = totalUsers ? (freeCount / totalUsers) * 100 : 0;

	// Source-specific plan data
	const marketingGold = getSourcePlan(marketingStats, "Gold");
	const marketingLuxury = getSourcePlan(marketingStats, "Luxury");
	const organicGold = getSourcePlan(organicStats, "Gold");
	const organicLuxury = getSourcePlan(organicStats, "Luxury");

	return (
		<Box
			p={2}
			sx={{
				boxShadow: "none",
				border: "1px solid #e6e6e6",
				borderRadius: "20px",
			}}
		>
			<Typography fontWeight="500" color="#3C4043" fontSize={20}>
				Revenue (New York)
			</Typography>

			<Typography fontWeight="600" mt={1} mb={2} fontSize={24} color="#3C4043">
				${totalRevenue.toLocaleString()}
			</Typography>

			{/* Progress Bar (user count ratio) */}
			<Box
				display="flex"
				width="100%"
				height={35}
				borderRadius={2}
				overflow="hidden"
			>
				<Box width={`${goldPercent}%`} bgcolor="#b98908" />
				<Box width={`${luxuryPercent}%`} bgcolor="#3d73f1" />
				<Box width={`${freePercent}%`} bgcolor="#e0e0e0" />
			</Box>

			{/* Legend */}
			<Stack
				direction="row"
				spacing={3}
				mt={1}
				sx={{ textAlign: "center", justifyContent: "center" }}
			>
				<Typography
					sx={{
						display: "flex",
						alignItems: "center",
						fontSize: 14,
						fontWeight: 500,
					}}
				>
					<FiberManualRecordIcon sx={{ fontSize: 15, color: "#CC9933" }} /> GOLD: $
					{goldRevenue.toLocaleString()} | {goldCount} Users (
					{goldPercent.toFixed(1)}%)
				</Typography>

				<Typography
					sx={{
						display: "flex",
						alignItems: "center",
						fontSize: 14,
						fontWeight: 500,
					}}
				>
					<FiberManualRecordIcon sx={{ fontSize: 15, color: "#3361FF" }} /> LUXURY: $
					{luxuryRevenue.toLocaleString()} | {luxuryCount} Users (
					{luxuryPercent.toFixed(1)}%)
				</Typography>

				<Typography
					sx={{
						display: "flex",
						alignItems: "center",
						fontSize: 14,
						fontWeight: 500,
					}}
				>
					<FiberManualRecordIcon sx={{ fontSize: 15, color: "#F1F1F1" }} /> Free: $
					{freeRevenue.toLocaleString()} | {freeCount} Users (
					{freePercent.toFixed(1)}%)
				</Typography>
			</Stack>

			{/* BY MARKETING / BY ORGANIC */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: 2,
					mt: 3,
				}}
			>
				{/* BY MARKETING */}
				<Box
					sx={{
						width: "100%",
						border: "1px solid #ccc",
						p: 3,
						borderRadius: 2,
						display: "flex",
						flexDirection: "column",
						gap: 1.5,
					}}
				>
					<Typography
						sx={{
							fontSize: "16px",
							color: "#3E3E3E",
							fontWeight: "500",
							textTransform: "uppercase",
						}}
					>
						BY MARKETING
					</Typography>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<Typography
							sx={{ fontSize: "24px", color: "#14AE5C", fontWeight: "600" }}
						>
							$
							{(
								marketingStats?.totalRevenue ?? 0
							).toLocaleString()}
						</Typography>
						<Typography
							variant="span"
							sx={{
								fontSize: "14px",
								color: "#3E3E3E",
								fontWeight: "500",
								border: "1px solid #ccc",
								borderRadius: 1,
								padding: "3px 10px",
							}}
						>
							{marketingStats
								? `${marketingStats.revenuePercentageOfTotal}%`
								: "0%"}
						</Typography>
					</Box>

					<Box>
						{/* GOLD row */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 1,
							}}
						>
							<Typography
								sx={{
									color: "#3C4043",
									fontSize: "14px",
									display: "flex",
									alignItems: "center",
									gap: 0.5,
								}}
							>
								<CircleIcon
									sx={{ color: "#CC9933", width: "12px", height: "12px" }}
								/>{" "}
								GOLD
							</Typography>
							<Box
								sx={{
									display: "flex",
									justifyContent: "end",
									alignItems: "center",
								}}
							>
								<Typography
									sx={{
										color: "#3C4043",
										fontSize: "14px",
										fontWeight: "600",
									}}
								>
									${marketingGold.totalRevenue.toLocaleString()}
								</Typography>
								<Typography
									sx={{
										fontSize: "12px",
										color: "#3C4043",
										minWidth: "70px",
										textAlign: "right",
									}}
								>
									{marketingGold.revenueShareOfPlan}%
								</Typography>
							</Box>
						</Box>

						{/* LUXURY row */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<Typography
								sx={{
									color: "#3C4043",
									fontSize: "14px",
									display: "flex",
									alignItems: "center",
									gap: 0.5,
								}}
							>
								<CircleIcon
									sx={{ color: "#3361FF", width: "12px", height: "12px" }}
								/>{" "}
								LUXURY
							</Typography>
							<Box
								sx={{
									display: "flex",
									justifyContent: "end",
									alignItems: "center",
								}}
							>
								<Typography
									sx={{
										color: "#3C4043",
										fontSize: "14px",
										fontWeight: "600",
									}}
								>
									${marketingLuxury.totalRevenue.toLocaleString()}
								</Typography>
								<Typography
									sx={{
										fontSize: "12px",
										color: "#3C4043",
										minWidth: "70px",
										textAlign: "right",
									}}
								>
									{marketingLuxury.revenueShareOfPlan}%
								</Typography>
							</Box>
						</Box>
					</Box>
				</Box>

				{/* BY ORGANIC */}
				<Box
					sx={{
						width: "100%",
						border: "1px solid #ccc",
						p: 3,
						borderRadius: 2,
						display: "flex",
						flexDirection: "column",
						gap: 1.5,
					}}
				>
					<Typography
						sx={{
							fontSize: "16px",
							color: "#3E3E3E",
							fontWeight: "500",
							textTransform: "uppercase",
						}}
					>
						BY ORGANIC
					</Typography>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<Typography
							sx={{ fontSize: "24px", color: "#14AE5C", fontWeight: "600" }}
						>
							$
							{(
								organicStats?.totalRevenue ?? 0
							).toLocaleString()}
						</Typography>
						<Typography
							variant="span"
							sx={{
								fontSize: "14px",
								color: "#3E3E3E",
								fontWeight: "500",
								border: "1px solid #ccc",
								borderRadius: 1,
								padding: "3px 10px",
							}}
						>
							{organicStats
								? `${organicStats.revenuePercentageOfTotal}%`
								: "0%"}
						</Typography>
					</Box>

					<Box>
						{/* GOLD row */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 1,
							}}
						>
							<Typography
								sx={{
									color: "#3C4043",
									fontSize: "14px",
									display: "flex",
									alignItems: "center",
									gap: 0.5,
								}}
							>
								<CircleIcon
									sx={{ color: "#CC9933", width: "12px", height: "12px" }}
								/>{" "}
								GOLD
							</Typography>
							<Box
								sx={{
									display: "flex",
									justifyContent: "end",
									alignItems: "center",
								}}
							>
								<Typography
									sx={{
										color: "#3C4043",
										fontSize: "14px",
										fontWeight: "600",
									}}
								>
									${organicGold.totalRevenue.toLocaleString()}
								</Typography>
								<Typography
									sx={{
										fontSize: "12px",
										color: "#3C4043",
										minWidth: "70px",
										textAlign: "right",
									}}
								>
									{organicGold.revenueShareOfPlan}%
								</Typography>
							</Box>
						</Box>

						{/* LUXURY row */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<Typography
								sx={{
									color: "#3C4043",
									fontSize: "14px",
									display: "flex",
									alignItems: "center",
									gap: 0.5,
								}}
							>
								<CircleIcon
									sx={{ color: "#3361FF", width: "12px", height: "12px" }}
								/>{" "}
								LUXURY
							</Typography>
							<Box
								sx={{
									display: "flex",
									justifyContent: "end",
									alignItems: "center",
								}}
							>
								<Typography
									sx={{
										color: "#3C4043",
										fontSize: "14px",
										fontWeight: "600",
									}}
								>
									${organicLuxury.totalRevenue.toLocaleString()}
								</Typography>
								<Typography
									sx={{
										fontSize: "12px",
										color: "#3C4043",
										minWidth: "70px",
										textAlign: "right",
									}}
								>
									{organicLuxury.revenueShareOfPlan}%
								</Typography>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>

			{isLoading && (
				<Typography mt={2} fontSize={12} color="#777">
					Loading New York revenue…
				</Typography>
			)}
		</Box>
	);
};

export default RevenueChartNewYork;
